import { FindingsTableItem } from "../../models/tables";
import { CommonFilterData } from "../../models/filters";
import { Table } from "../utils";

type CompareFunction = (a: FindingsTableItem, b: FindingsTableItem) => number;

export class FindingTable extends Table {
  private sortFunctions: Record<string, CompareFunction> = {};

  constructor(data: FindingsTableItem[]) {
    super(data);
    this.initSortFunctions();
    this.tableData = this.groupFindings();
  }

  private initSortFunctions(): void {
    const createCompareFunction = (getValueFn: (item: FindingsTableItem) => string | number | Date | string[]): CompareFunction => {
      return (a, b) => {
        const valueA = getValueFn(a);
        const valueB = getValueFn(b);

        // Special handling for arrays (stages/branches)
        if (Array.isArray(valueA) && Array.isArray(valueB)) {
          return this.compareArrays(valueA, valueB);
        }

        if (valueA === valueB) return 0;
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }
        return valueA < valueB ? -1 : 1;
      };
    };

    this.sortFunctions = {
      name: createCompareFunction(item => item.name || ''),
      project: createCompareFunction(item => item.project?.name || ''),
      package: createCompareFunction(item => `${item.details.package_id} ${item.details.package_spec}`),
      location: createCompareFunction(item => item.details.location || ''),
      date_last_seen: createCompareFunction(item => new Date(item.date_last_seen)),
      status: createCompareFunction(item => item.status || ''),
      branches: (a, b) => this.compareBranches(a, b),
      stages: createCompareFunction(item => item.stages),
      exploitability_score: (a, b) =>
        (b.exploitability_score ?? -Infinity) - (a.exploitability_score ?? -Infinity), // Descending order
      cvss_score: (a, b) =>
        (b.cvss_score ?? -Infinity) - (a.cvss_score ?? -Infinity),
      default: createCompareFunction(item => String(item[this.sortCol as keyof FindingsTableItem]))
    };
  }

  private compareArrays(a: string[], b: string[]): number {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      const comparison = a[i].localeCompare(b[i]);
      if (comparison !== 0) return comparison;
    }
    return a.length - b.length;
  }

  private compareBranches(a: FindingsTableItem, b: FindingsTableItem): number {
    const aHasDefault = a.project ? a.branches.includes(a.project.default_branch) : false;
    const bHasDefault = b.project ? b.branches.includes(b.project.default_branch) : false;

    if (aHasDefault && !bHasDefault) return -1;
    if (!aHasDefault && bHasDefault) return 1;

    const aSortedBranches = this.sortBranches(a.branches, a.project?.default_branch || '');
    const bSortedBranches = this.sortBranches(b.branches, b.project?.default_branch || '');

    return this.compareArrays(aSortedBranches, bSortedBranches);
  }

  private sortBranches(branches: string[], defaultBranch: string): string[] {
    return branches.sort((a, b) => {
      if (a === defaultBranch) return -1;
      if (b === defaultBranch) return 1;
      return a.localeCompare(b);
    });
  }

  groupFindings(): FindingsTableItem[] {
    const groupedMap = new Map<string, FindingsTableItem>();
    this.constantTableData.forEach((finding: FindingsTableItem) => {
      const existingFinding = groupedMap.get(finding.unique_identifier);
      if (!existingFinding || finding.date_last_seen > existingFinding.date_last_seen) {
        groupedMap.set(finding.unique_identifier, finding);
      }
    });
    return Array.from(groupedMap.values());
  }

  sort(col: string): void {
    this.sortAsc = this.sortCol === col ? !this.sortAsc : false;
    this.sortCol = col;

    const compareFunction = this.sortFunctions[col] || this.sortFunctions.default;
    this.tableData.sort((a: FindingsTableItem, b: FindingsTableItem) => {
      const result = compareFunction(a, b);
      return this.sortAsc ? -result : result;
    });
  }

  filterTable(filterData: CommonFilterData[]): void {
    const activeFilters = filterData.filter(filter => filter.options.some(option => option.checked));

    if (activeFilters.length === 0) {
      this.tableData = this.groupFindings();
    } else {
      const filteredData: FindingsTableItem[] = this.constantTableData.filter((item: FindingsTableItem) =>
        activeFilters.every((filter: CommonFilterData) => this.applyFilter(filter, item))
      );
      this.tableData = this.groupFindingsArray(filteredData);
    }

    this.curPage = this.tableData.length < this.pageSize ? 1 : this.curPage;
  }

  private applyFilter(filter: CommonFilterData, item: FindingsTableItem): boolean {
    const validOptions = filter.options.filter(option => option.checked);
    return validOptions.some(option => {
      let comparisonValue: string | undefined;

      switch (filter.col) {
        case 'project':
          comparisonValue = item.project?.name;
          break;
        case 'package':
          comparisonValue = `${item.details?.package_id ?? ""} ${item.details?.package_spec ?? ""}`;
          break;
        case 'branch_name':
          comparisonValue = item.branch_name;
          break;
        case 'location':
          comparisonValue = item.details?.location;
          break;
        default:
          comparisonValue = item[filter.col as keyof FindingsTableItem] as string | undefined;
      }

      return option.value === comparisonValue;
    });
  }

  private groupFindingsArray(findings: FindingsTableItem[]): FindingsTableItem[] {
    const groupedMap = new Map<string, FindingsTableItem>();
    findings.forEach(finding => {
      const existingFinding = groupedMap.get(finding.unique_identifier);
      if (!existingFinding || finding.date_last_seen > existingFinding.date_last_seen) {
        groupedMap.set(finding.unique_identifier, finding);
      }
    });
    return Array.from(groupedMap.values());
  }
}

export const elevated = (data: FindingsTableItem[]): FindingsTableItem[] =>
  data.filter(row => row.risk_score?.label === "critical");

export const fixVulnerabilityTab = () => ({
  tabItems: [
    { label: "Pip", isActive: true },
    { label: "Poetry", isActive: false },
    { label: "Pipenv", isActive: false },
  ],
  switchTabs(tabItem: string) {
    this.tabItems = this.tabItems.map(item => ({
      ...item,
      isActive: item.label === tabItem
    }));
  },
});

/**
 * TableTooltip component for handling hover tooltips in the findings table
 * Used to display informational tooltips for EPSS and Severity columns.
 */
export const TableTooltip = () => {
  // Define types for Alpine component
  type AlpineComponent = {
    $el: HTMLElement;
    $watch: (property: string, callback: (value: any) => void) => void;
  }

  // Define the type for custom tooltip events
  interface TooltipEventDetail {
    id: string;
    text: string;
    clientX?: number;
    clientY?: number;
  }

  // Define component type with all properties and methods
  interface TooltipState {
    showTooltip: boolean;
    tooltipText: string;
    tooltipId: string;
    tooltipLeft: number;
    tooltipTop: number;
    init: (this: AlpineComponent & TooltipState) => void;
    updatePosition: (this: AlpineComponent & TooltipState, e: Event | CustomEvent<TooltipEventDetail>) => void;
  }

  return {
    showTooltip: false,
    tooltipText: '',
    tooltipId: '',
    tooltipLeft: 0,
    tooltipTop: 0,

    init(this: AlpineComponent & TooltipState) {
      // Ensure tooltip is completely hidden on initialization
      this.$el.style.display = 'none';
      this.$el.style.visibility = 'hidden';
      this.$el.setAttribute('style', 'z-index: 9999; position: fixed; display: none; visibility: hidden;');

      this.$watch('showTooltip', (value: boolean) => {
        if (value) {
          this.$el.style.display = 'block';
          this.$el.style.visibility = 'visible';
        } else {
          this.$el.style.display = 'none';
          this.$el.style.visibility = 'hidden';
        }
      });

      window.addEventListener('show-tooltip', ((e: Event) => {
        const customEvent = e as CustomEvent<TooltipEventDetail>;
        const tooltipInfo = customEvent.detail;

        this.tooltipId = tooltipInfo.id;
        this.tooltipText = tooltipInfo.text;

        // Position at cursor
        this.updatePosition(customEvent);

        // Show after positioning is complete
        this.showTooltip = true;
      }) as EventListener);

      window.addEventListener('move-tooltip', ((e: Event) => {
        if (this.showTooltip) {
          this.updatePosition(e as CustomEvent<TooltipEventDetail>);
        }
      }) as EventListener);

      window.addEventListener('hide-tooltip', (() => {
        this.showTooltip = false;
        this.tooltipId = '';
        this.tooltipText = '';
      }) as EventListener);
    },

    updatePosition(this: AlpineComponent & TooltipState, e: Event | CustomEvent<TooltipEventDetail>) {
      // Get cursor position from event
      let clientX = 0;
      let clientY = 0;

      if ('clientX' in e && typeof e.clientX === 'number' && 'clientY' in e && typeof e.clientY === 'number') {
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        const customEvent = e as CustomEvent<TooltipEventDetail>;
        if (customEvent.detail) {
          clientX = customEvent.detail.clientX || 0;
          clientY = customEvent.detail.clientY || 0;
        }
      }

      // Set position with small offset
      this.tooltipLeft = clientX + 15;
      this.tooltipTop = clientY + 15;

      // Ensure tooltip stays within viewport
      const tooltipWidth = 240; // Approximate width of tooltip
      const tooltipHeight = 80; // Approximate height of tooltip
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (this.tooltipLeft + tooltipWidth > viewportWidth) {
        this.tooltipLeft = viewportWidth - tooltipWidth - 10;
      }

      if (this.tooltipTop + tooltipHeight > viewportHeight) {
        this.tooltipTop = clientY - tooltipHeight - 10;
      }
    }
  };
};
